import { ref, getDownloadURL } from "firebase/storage";
import { storage, ensureAuthenticated } from "@/lib/firebase";
import { getStorage } from "firebase/storage";
import { getFirebaseApp } from "@/lib/firebaseClient";

const inMemoryCache = new Map<string, string>();

/**
 * Clear cached URL for a specific object path
 * Useful when URLs expire or need to be refreshed
 */
export function clearCachedUrl(objectPath: string): void {
  inMemoryCache.delete(objectPath);
}

/**
 * Clear all cached URLs
 */
export function clearAllCachedUrls(): void {
  inMemoryCache.clear();
}

/**
 * Get public video URL without authentication (for showcase videos)
 * This doesn't require user authentication since showcase-videos have public read access
 * Uses a separate storage instance that's not tied to authentication
 * 
 * Note: Firebase Storage download URLs can expire after ~1 hour. If video playback stops,
 * the URL may have expired and needs to be refreshed.
 */
export async function getPublicVideoUrl(objectPath: string, forceRefresh: boolean = false): Promise<string> {
  if (!objectPath) {
    throw new Error("[storageUrls] objectPath is required");
  }
  
  // Clear cache if force refresh is requested
  if (forceRefresh) {
    inMemoryCache.delete(objectPath);
  }
  
  const cached = inMemoryCache.get(objectPath);
  if (cached) return cached;

  try {
    // Use a storage instance from firebaseClient which doesn't require authentication
    // This ensures public files can be accessed without auth
    const publicStorage = getStorage(getFirebaseApp());
    const storageRef = ref(publicStorage, objectPath);
    
    // Try to get the download URL
    // Even for public files, getDownloadURL might work without auth if rules allow it
    const url = await getDownloadURL(storageRef);
    inMemoryCache.set(objectPath, url);
    return url;
  } catch (error: any) {
    // If getDownloadURL fails due to auth requirements, try with authenticated storage
    if (error.code === 'storage/unauthorized' || 
        error.code === 'storage/permission-denied' ||
        error.message?.includes('permission') ||
        error.message?.includes('unauthorized')) {
      
      console.warn(`[storageUrls] getDownloadURL failed for ${objectPath}, trying with authentication...`);
      
      try {
        // Try authenticating and then getting the URL
        // This handles cases where storage rules require some form of auth context
        await ensureAuthenticated();
        const storageRef = ref(storage, objectPath);
        const url = await getDownloadURL(storageRef);
        inMemoryCache.set(objectPath, url);
        return url;
      } catch (authError: any) {
        // If authentication also fails, construct a direct public URL as last resort
        // Note: This will only work if the file is truly public and storage rules allow it
        const projectId = process.env.APPCONSTANTS_LAUNCHPAD_LAUNCHPADVITE_FIREBASE_PROJECT_ID || 'sfy-launchpad';
        const bucket = process.env.APPCONSTANTS_LAUNCHPAD_LAUNCHPADVITE_FIREBASE_BUCKET || `${projectId}.firebasestorage.app`;
        
        // Encode the path for URL
        const encodedPath = encodeURIComponent(objectPath);
        const publicUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
        
        console.warn(`[storageUrls] Using direct public URL for ${objectPath}`);
        console.warn(`[storageUrls] Original error: ${error.message}`);
        console.warn(`[storageUrls] Auth error: ${authError.message}`);
        console.warn(`[storageUrls] Note: Ensure storage rules are deployed with 'allow read: if true;' for showcase-videos`);
        
        inMemoryCache.set(objectPath, publicUrl);
        return publicUrl;
      }
    }
    
    // Re-throw other errors
    throw error;
  }
}

/**
 * Get video URL with authentication (for protected content)
 * Requires user to be authenticated via SSO
 */
export async function getVideoUrl(objectPath: string): Promise<string> {
  if (!objectPath) {
    throw new Error("[storageUrls] objectPath is required");
  }
  
  const cached = inMemoryCache.get(objectPath);
  if (cached) return cached;

  // Ensure user is authenticated to Firebase
  await ensureAuthenticated();
  
  const storageRef = ref(storage, objectPath);
  const url = await getDownloadURL(storageRef);
  inMemoryCache.set(objectPath, url);
  return url;
}


